import React from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Ce qui demande une action aujourd'hui, réduit à une icône d'alerte animée
 * dans le coin de l'accueil. Elle remplace la ligne pleine largeur
 * « À TRAITER · … »: la pastille donne le nombre, un clic (ou Entrée) ouvre
 * la liste, et chaque ligne mène à l'écran où l'on agit.
 *
 * `elements`: `[{ id, nombre, libelle, onClick, testid }]`. Les lignes à zéro
 * sont ignorées; sans rien à traiter, rien ne s'affiche.
 */
export const AlerteATraiter = ({ elements = [], className }) => {
  const [ouvert, setOuvert] = React.useState(false);
  const lignes = elements.filter((element) => Number(element?.nombre) > 0);
  if (lignes.length === 0) return null;
  const total = lignes.reduce((somme, element) => somme + Number(element.nombre), 0);
  const libelleTotal = `${total} point${total > 1 ? 's' : ''} à traiter`;

  return (
    <Popover open={ouvert} onOpenChange={setOuvert}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={libelleTotal}
          title={libelleTotal}
          data-testid="attention-alert"
          className={cn(
            'acj-alerte relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
            'border border-destructive/30 bg-destructive/10 text-destructive shadow-sm',
            'transition-colors hover:bg-destructive/15 focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-destructive/50',
            className,
          )}
        >
          <AlertTriangle className="acj-alerte-icone h-5 w-5" aria-hidden="true" />
          <span
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold leading-none text-destructive-foreground"
            aria-hidden="true"
          >
            {total}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid="attention-popover">
        <p className="px-2 pb-1.5 pt-1 text-xs font-bold uppercase tracking-wide text-destructive">
          À traiter
        </p>
        <ul className="space-y-0.5">
          {lignes.map((element) => (
            <li key={element.id}>
              <button
                type="button"
                data-testid={element.testid}
                onClick={() => {
                  setOuvert(false);
                  element.onClick?.();
                }}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm font-semibold text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              >
                <span>{element.libelle}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default AlerteATraiter;
